import { ONBOARDING_STEP } from '@schema/initiative'
import { PhaseType } from '@schema/phase'

export const initiativeCreationSteps = {
  phaseSelection: 'phaseSelection',
  ...ONBOARDING_STEP,
} as const
export type InitiativeCreationStep = keyof typeof initiativeCreationSteps

const {
  phaseSelection,
  livingVision,
  images,
  initiativeDetails,
  livingTogether,
  livingAmenities,
  location,
  contact,
  finish,
} = initiativeCreationSteps

const nonLivingSteps = [
  phaseSelection,
  livingVision,
  images,
  initiativeDetails,
  livingTogether,
  location,
  contact,
  finish,
]

export const creationWorkflow: Record<PhaseType, InitiativeCreationStep[]> = {
  idea: [...nonLivingSteps],
  location: [...nonLivingSteps],
  design: [...nonLivingSteps],
  construction: [...nonLivingSteps],
  living: [
    phaseSelection,
    livingVision,
    images,
    initiativeDetails,
    livingTogether,
    livingAmenities,
    contact,
    finish,
  ],
}

export type CreationWorkflowOptions = {
  skipPhaseSelection?: boolean
}

export const getCreationWorkflow = (
  phaseType: PhaseType | null | undefined,
  options: CreationWorkflowOptions = {},
): InitiativeCreationStep[] => {
  const workflow = phaseType ? creationWorkflow[phaseType] : [phaseSelection]

  if (options.skipPhaseSelection) {
    return workflow.filter((workflowStep) => workflowStep !== phaseSelection)
  }

  return workflow
}

export const creationGuideDisplay = (
  phase: PhaseType | null,
  currentStep: InitiativeCreationStep,
): InitiativeCreationStep[] => {
  const steps = phase ? listedSteps(creationWorkflow[phase]) : []
  const guideDisplay: Partial<
    Record<InitiativeCreationStep, InitiativeCreationStep[]>
  > = {
    phaseSelection: [phaseSelection],
    livingVision: steps,
    images: steps,
    initiativeDetails: steps,
    livingTogether: steps,
    livingAmenities: steps,
    location: steps,
    contact: steps,
    finish: [finish],
  }

  return guideDisplay[currentStep] ?? steps
}

export const unlistedSteps: InitiativeCreationStep[] = [
  phaseSelection,
  finish,
] as const

export const listedSteps = (
  steps: InitiativeCreationStep[],
): InitiativeCreationStep[] =>
  steps.filter((step) => !unlistedSteps.includes(step))

export const getListedCreationWorkflowSteps = (
  phaseType: PhaseType | null | undefined,
  options: CreationWorkflowOptions = {},
): InitiativeCreationStep[] =>
  listedSteps(getCreationWorkflow(phaseType, options))

/** Single source of truth: whether an onboarding step exists for this phase (e.g. about-page sections). */
export const phaseWorkflowIncludes = (
  phaseType: PhaseType,
  step: InitiativeCreationStep,
): boolean => creationWorkflow[phaseType].includes(step)
